/**
 * write_plan MCP tool — exposes `mcp__appostle__write_plan` to agent sessions.
 *
 * The tool serializes a plan as YAML frontmatter + markdown body and writes it
 * to `.plans/{slug}.md` under the agent's cwd. The frontmatter contract that
 * used to live in the system prompt (plan-mode block) is now expressed as the
 * Zod input schema — typed, validated, no model-formatted YAML.
 *
 * In plan mode the canUseTool callback (claude-agent.ts) blocks raw `Write`
 * calls to `.plans/*.md`, so this is the only path to create a plan from a
 * plan-mode session.
 */

import fs from "node:fs";
import path from "node:path";
import type { Logger } from "pino";
import { z } from "zod";
import type { AgentManager } from "./agent-manager.js";
import { resolvePlanFilename, slugifyPlanName } from "./plan-filename.js";

const TODO_STATUSES = ["pending", "in_progress", "needs_help", "completed"] as const;

export interface WritePlanToolOptions {
  agentManager: AgentManager;
  logger: Logger;
}

export const WRITE_PLAN_DESCRIPTION = [
  "Write a plan file to `.plans/{slug}.md` under the agent's working directory.",
  "Serializes structured input as YAML frontmatter + markdown body so the Appostle Plans modal can render an interactive checklist.",
  "Use this in plan mode — the raw `Write` tool is blocked for `.plans/*.md` paths in plan mode.",
  "Markdown body supports ```mermaid blocks (live-rendered) and regular fenced code.",
].join("\n");

export const writePlanSchema = {
  name: z.string().min(1).describe("Plan title in sentence case."),
  overview: z.string().min(1).describe("One or two sentence summary of what the plan achieves."),
  todos: z
    .array(
      z.object({
        id: z
          .string()
          .min(1)
          .regex(/^[a-z0-9][a-z0-9-]*$/, "id must be kebab-case")
          .describe("Short kebab-case slug, stable and unique within the plan."),
        content: z.string().min(1).describe("One concrete, user-facing action."),
        status: z
          .enum(TODO_STATUSES)
          .describe(
            'Use "pending" for new plans. "needs_help" only when a future implementer is blocked on user input.',
          ),
      }),
    )
    .min(2)
    .max(10)
    .describe("2–10 concrete todos covering the real work. No filler."),
  isProject: z
    .boolean()
    .describe("false for single-task plans, true for long-running multi-phase projects."),
  body: z
    .string()
    .min(1)
    .describe(
      "Markdown body rendered below the frontmatter. Supports ```mermaid blocks (live diagrams). Avoid nested fences of the same length — use ~~~ outer + ``` inner if needed.",
    ),
  slug: z
    .string()
    .optional()
    .describe("Optional kebab-case filename (without .md). Auto-derived from name if omitted."),
};

type WritePlanArgs = z.infer<z.ZodObject<typeof writePlanSchema>>;

type ToolResult = {
  isError?: boolean;
  content: Array<{ type: "text"; text: string }>;
};

export function buildWritePlanHandler(
  callerAgentId: string,
  options: WritePlanToolOptions,
): (args: WritePlanArgs) => Promise<ToolResult> {
  const { agentManager, logger } = options;
  const log = logger.child({ module: "write-plan-tool", callerAgentId });

  return async (args) => {
    const agent = agentManager.getAgentSnapshot(callerAgentId);
    if (!agent) {
      return {
        isError: true,
        content: [{ type: "text", text: `Caller agent ${callerAgentId} not found` }],
      };
    }

    const slug = (
      args.slug && args.slug.length > 0 ? args.slug : slugifyPlanName(args.name)
    ).trim();
    if (slug.length === 0) {
      return {
        isError: true,
        content: [
          { type: "text", text: "Could not derive a slug from `name` — pass `slug` explicitly." },
        ],
      };
    }

    const plansDir = path.join(agent.cwd, ".plans");
    try {
      fs.mkdirSync(plansDir, { recursive: true });
    } catch (err) {
      log.error({ err, plansDir }, "write_plan: mkdir failed");
      return {
        isError: true,
        content: [
          {
            type: "text",
            text: `Could not create .plans directory: ${err instanceof Error ? err.message : String(err)}`,
          },
        ],
      };
    }

    const targetPath = resolvePlanFilename({
      originalPath: path.join(plansDir, `${slug}.md`),
      newSlug: slug,
      existsSync: fs.existsSync,
    });

    const frontmatter = serializeFrontmatter({
      name: args.name,
      overview: args.overview,
      todos: args.todos,
      isProject: args.isProject,
    });
    const body = args.body.startsWith("\n") ? args.body : `\n${args.body}`;
    const fileContent = `${frontmatter}${body}${body.endsWith("\n") ? "" : "\n"}`;

    try {
      fs.writeFileSync(targetPath, fileContent, "utf8");
    } catch (err) {
      log.error({ err, targetPath }, "write_plan: writeFile failed");
      return {
        isError: true,
        content: [
          {
            type: "text",
            text: `Failed to write plan file: ${err instanceof Error ? err.message : String(err)}`,
          },
        ],
      };
    }

    log.info({ targetPath, slug, todoCount: args.todos.length }, "write_plan: wrote plan");
    return {
      content: [{ type: "text", text: `Plan written to ${targetPath}` }],
    };
  };
}

interface FrontmatterInput {
  name: string;
  overview: string;
  todos: ReadonlyArray<{ id: string; content: string; status: (typeof TODO_STATUSES)[number] }>;
  isProject: boolean;
}

function yamlEscape(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function serializeFrontmatter(input: FrontmatterInput): string {
  const lines = ["---"];
  lines.push(`name: "${yamlEscape(input.name)}"`);
  lines.push(`overview: "${yamlEscape(input.overview)}"`);
  lines.push("todos:");
  for (const todo of input.todos) {
    lines.push(`  - id: ${todo.id}`);
    lines.push(`    content: "${yamlEscape(todo.content)}"`);
    lines.push(`    status: ${todo.status}`);
  }
  lines.push(`isProject: ${input.isProject ? "true" : "false"}`);
  lines.push("---\n");
  return lines.join("\n");
}
